/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import 'reflect-metadata';
import './polyfills';
import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { enableProdMode, ErrorHandler, importProvidersFrom, inject, Injector, provideAppInitializer } from '@angular/core';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { bootstrapApplication } from '@angular/platform-browser';
import { BulkUploadState } from '@app/components/bulk-upload/state/bulk-upload.state';
import { GsptService } from '@app/components/gspt/gspt.service';
import { LineIsolationService } from '@app/components/isolation/line-isolation.service';
import { RedlineConfigService } from '@app/components/redline/services/redline-config.service';
import { RedlineService } from '@app/components/redline/services/redline.service';
import { RedlineState } from '@app/components/redline/state/redline.state';
import { RedlineMarkupState } from '@app/components/redlineMarkup/state/redlineMarkup.state';
import { DigitizePointReportModule } from '@app/elements/digitizePointReport/digitize-point-report.module';
import { DocumentLinkModule } from '@app/elements/documentLink/document-link.module';
import { FileDownloadModule } from '@app/elements/fileDownload/file-download.module';
import { QrCodeModule } from '@app/elements/qr-code/qr-code.module';
import { ReportButtonModule } from '@app/elements/reportButtons/report-button.module';
import { ReportEditButtonModule } from '@app/elements/reportButtons/report-edit-button.module';
import { ZoomButtonElementModule } from '@app/elements/zoomButton/zoom-button-element.module';

// install modernizr tests
import { customModernizrTests } from '@app/modernizr.custom';
import { withNgxsResetPlugin } from '@app/ngxs/resetPlugin/ngxs-reset.module';
import { withNgxsSnapshotPlugin } from '@app/ngxs/snapshotPlugin/ngxs-snapshot-plugin.module';
import { RootComponent } from '@core/root/root.component';
import { provideAppRouting } from '@core/routing/app.routes';
import { LogoutModule } from '@core/security/components/logout/logout.module';
import { ResetPasswordModule } from '@core/security/components/password/reset-password.module';
import { UserToolsModule } from '@core/security/components/user-tools/user-tools.module';
import { csJwtOptionsProvider } from '@core/security/csJwtOptions.provider';
import { CheckTokenInterceptor } from '@core/security/interceptors/check-token.interceptor';
import { ErrorHttpInterceptor } from '@core/security/interceptors/error.httpInterceptor';
import { ForcePasswordChangeInterceptor } from '@core/security/interceptors/force-password-change.interceptor';
import { UnauthorizedInterceptor } from '@core/security/interceptors/unauthorized.interceptor';
import { IsAuthenticatedTokens } from '@core/security/is-authenticated-tokens';
import { OpenIdStorageShim } from '@core/security/open-id-storage-shim';
import { AuthService } from '@core/security/services/auth.service';
import { OpenIdAuthenticatedService } from '@core/security/services/open-id-authenticated.service';
import { PasswordAuthenticatedService } from '@core/security/services/password-authenticated.service';
import { LoggedOut } from '@core/security/state/auth.actions';
import { AuthState } from '@core/security/state/auth.state';
import { AppConfigState } from '@core/state/appConfig/app-config.state';
import { NotificationState } from '@core/state/notification/notification.state';
import { PageState } from '@core/state/page/page.state';
import { ResizableListenerService } from '@core/ui/dash-gis-ng2-resizable/resizableListener.service';
import { HotKeyService } from '@core/ui/hotkeys/hot-key.service';
import { DashGisOverlayContainer } from '@core/ui/overlay/dash-gis-overlay-container.service';
import { DashGisOverlay } from '@core/ui/overlay/dash-gis-overlay.service';
import { WindowRefService } from '@core/ui/services/windowRef.service';
import { ThemeState } from '@core/ui/theme-switcher/state/theme.state';
import { ThemeSwitcherModule } from '@core/ui/theme-switcher/theme-switcher.module';

import { env } from '@env/environment-factory';
import { JWT_OPTIONS, provideJwtConfig } from '@jjmhalew/angular-jwt';
import { ATTACH_COMMAND } from '@map/command/attach/attach.token';
import { attachFactory } from '@map/command/attach/attachFactory';
import { BufferService } from '@map/command/buffer/buffer.service';
import { clearBufferFactory } from '@map/command/buffer/clear-buffer/clear-buffer.factory';
import { CLEAR_BUFFER_COMMAND } from '@map/command/buffer/clear-buffer/clear-buffer.token';
import { createBufferFactory } from '@map/command/buffer/create-buffer/create-buffer.factory';
import { CREATE_BUFFER_COMMAND } from '@map/command/buffer/create-buffer/create-buffer.token';
import { selectWithinBufferFactory } from '@map/command/buffer/select-within-buffer/select-within-buffer.factory';
import { SELECT_WITHIN_BUFFER_COMMAND } from '@map/command/buffer/select-within-buffer/select-within-buffer.token';
import { copyLatLonFactory } from '@map/command/copy-lat-lon/copy-lat-lon.factory';
import { COPY_LAT_LON_COMMAND } from '@map/command/copy-lat-lon/copy-lat-lon.token';
import { dataReportFactory } from '@map/command/data-report/data-report.factory';
import { DATA_REPORT_COMMAND } from '@map/command/data-report/data-report.token';
import { deselectFactory } from '@map/command/deselect/deselect.factory';
import { DESELECT_COMMAND } from '@map/command/deselect/deselect.token';
import { DigitizePointConfigService } from '@map/command/digitize-point/digitize-point-config.service';
import { DigitizePointLauncherService } from '@map/command/digitize-point/digitize-point-launcher.service';
import { digitizePointFactory } from '@map/command/digitize-point/digitize-point.factory';
import { DigitizePointService } from '@map/command/digitize-point/digitize-point.service';
import { DIGITIZE_POINT_COMMAND } from '@map/command/digitize-point/digitize-point.token';
import { DigitizePointConfigState } from '@map/command/digitize-point/state/digitize-point-config.state';
import { launchGoogleMapsFactory } from '@map/command/launch-google-maps/launch-google-maps.factory';
import { LAUNCH_GOOGLE_MAPS_COMMAND } from '@map/command/launch-google-maps/launch-google-maps.token';
import { launchUrlFactory } from '@map/command/launch-url/launch-url.factory';
import { LAUNCH_URL_COMMAND } from '@map/command/launch-url/launch-url.token';
import { liveSearchFactory } from '@map/command/live-search/live-search.factory';
import { LIVE_SEARCH_COMMAND } from '@map/command/live-search/live-search.token';
import { LiveSearchService } from '@map/command/live-search/liveSearch.service';
import { logoutFactory } from '@map/command/logout/logout.factory';
import { LOGOUT_COMMAND } from '@map/command/logout/logout.token';
import { MapCommandLocatorService } from '@map/command/map-command-locator.service';
import { markupFactory } from '@map/command/markup/markup.factory';
import { MARKUP_COMMAND } from '@map/command/markup/markup.token';
import { measureFactory } from '@map/command/measure/measure.factory';
import { MEASURE_COMMAND } from '@map/command/measure/measure.token';
import { panFactory } from '@map/command/pan/pan.factory';
import { PAN_COMMAND } from '@map/command/pan/pan.token';
import { polygonSelectFactory } from '@map/command/polygon-select/polygon-select.factory';
import { PolygonSelectModule } from '@map/command/polygon-select/polygon-select.module';
import { POLYGON_SELECT_COMMAND } from '@map/command/polygon-select/polygon-select.token';
import { PolygonSelectState } from '@map/command/polygon-select/state/polygon-select.state';
import { printFactory } from '@map/command/print/print.factory';
import { PRINT_COMMAND } from '@map/command/print/print.token';
import { PrintOptionsState } from '@map/command/print/state/print-options.state';
import { reloadFactory } from '@map/command/reload/reload.factory';
import { RELOAD_COMMAND } from '@map/command/reload/reload.token';
import { searchFactory } from '@map/command/search/search.factory';
import { SEARCH_COMMAND } from '@map/command/search/search.token';
import { selectFactory } from '@map/command/select/select.factory';
import { SELECT_COMMAND } from '@map/command/select/select.token';
import { zoomExtentsFactory } from '@map/command/zoom-extents/zoom-extents.factory';
import { ZOOM_EXTENTS_COMMAND } from '@map/command/zoom-extents/zoom-extents.token';
import { zoomInFactory } from '@map/command/zoom-in/zoom-in.factory';
import { ZOOM_IN_COMMAND } from '@map/command/zoom-in/zoom-in.token';
import { zoomLatLonFactory } from '@map/command/zoom-lat-lon/zoom-lat-lon.factory';
import { ZoomLatLonService } from '@map/command/zoom-lat-lon/zoom-lat-lon.service';
import { ZOOM_LAT_LON_COMMAND } from '@map/command/zoom-lat-lon/zoom-lat-lon.token';
import { zoomOutFactory } from '@map/command/zoom-out/zoom-out.factory';
import { ZOOM_OUT_COMMAND } from '@map/command/zoom-out/zoom-out.token';
import { FeatureService } from '@map/services/feature.service';
import { GoogleMapService } from '@map/services/googleMap.service';
import { LocationService } from '@map/services/location.service';
import { MapConfigService } from '@map/services/map-config.service';
import { MapService } from '@map/services/map.service';
import { MapInteractionsService } from '@map/services/mapInteractions.service';
import { OrientationService } from '@map/services/orientation.service';
import { QueryService } from '@map/services/query.service';
import { ActiveMapState } from '@map/state/active-map.state';
import { MapConfigState } from '@map/state/map-config.state';
import { SelectionState } from '@map/state/selection.state';
import { ActiveToolState } from '@map/toolbar/active-tool/state/active-tool.state';
import { LAYOUT_CONFIG } from '@ngbracket/ngx-layout';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { STORAGE_ENGINE, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore, Store } from '@ngxs/store';
import { UploaderState } from '@reports/interface/reportControls/upload/state/upload.state';
import { DataFormService } from '@reports/services/data-form.service';
import { ReportDisplayService } from '@reports/services/report-display.service';
import { ReportVideoLauncherService } from '@reports/services/report-video-launcher.service';
import { ReportService } from '@reports/services/report.service';
import { DataFormState } from '@reports/state/data-form.state';
import {
    SelectDataReport,
    SelectDataReportByName,
    SelectFeatureReport,
    SelectFeatureReportByName,
    SetActiveDataReports,
    SetActiveFeatureReports
} from '@reports/state/report.actions';
import { ReportState } from '@reports/state/report.state';
import { SearchService } from '@search/services/search.service';
import { SearchState } from '@search/state/search.state';
import { DashGisErrorHandler } from '@shared/dash-gis-error-handler.service';
import { LocalStorageProxy } from '@shared/local-storage-shim';
import { PipeModule } from '@shared/pipes/pipe.module';
import { BackendLoggerService } from '@shared/services/backendLogger.service';
import { ConsoleLoggerService } from '@shared/services/consoleLogger.service';
import { DeviceService } from '@shared/services/device.service';
import { DownloadService } from '@shared/services/download.service';
import { IconLoaderService } from '@shared/services/iconLoader.service';
import { LOGGER_PROVIDERS, LoggerService } from '@shared/services/logger.service';
import { MeasureService } from '@shared/services/measure.service';
import { QrCodeService } from '@shared/services/qr-code.service';
import { RestService } from '@shared/services/rest.service';
import { ZoomService } from '@shared/services/zoom.service';
import { OAuthLogger, OAuthService, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';

customModernizrTests();

// videojs
(window as any).VIDEOJS_NO_DYNAMIC_STYLE = true;

if (env.production) {
    enableProdMode();
}

bootstrapApplication(RootComponent, {
    providers: [
        provideStore([
            ActiveMapState,
            ActiveToolState,
            AppConfigState,
            AuthState,
            BulkUploadState,
            DataFormState,
            DigitizePointConfigState,
            MapConfigState,
            NotificationState,
            PageState,
            PolygonSelectState,
            PrintOptionsState,
            RedlineMarkupState,
            RedlineState,
            ReportState,
            SearchState,
            SelectionState,
            ThemeState,
            UploaderState,
        ], {
            developmentMode: !env.production,
            selectorOptions: {
                // defaults for both will change to false in Ngxs v4
                injectContainerState: true,
                suppressErrors: false
            }
        },
            withNgxsStoragePlugin({
                keys: [
                    'auth.requestedRoute',
                    'auth.activeMethod',
                    'auth.needsDisclaimer',
                    'printOptions',
                    'theme.variant'
                ]
            }),
            withNgxsFormPlugin(),
            withNgxsRouterPlugin(),
            withNgxsSnapshotPlugin({
                groups: [
                    {
                        name: 'featureReports',
                        triggers: [ SetActiveFeatureReports, SelectFeatureReport, SelectFeatureReportByName ],
                        keys: [
                            { key: 'reports.activeFeatureReports', required: true },
                            { key: 'reports.selectedFeatureReports' },
                            { key: 'reports.featureReportKeys' },
                            { key: 'reports.options' }
                        ]
                    },
                    {
                        name: 'dataReports',
                        triggers: [ SetActiveDataReports, SelectDataReport, SelectDataReportByName ],
                        keys: [
                            { key: 'reports.activeDataReports', required: true },
                            { key: 'reports.selectedDataReport' },
                            { key: 'reports.options' }
                        ]
                    }
                ]
            }),
            withNgxsResetPlugin({
                trigger: LoggedOut,
                preserve: [
                    'appConfig',
                    'auth.loaded',
                    'auth.needsDisclaimer',
                    'auth.methods',
                    'auth.isInitialVisit',
                    'auth.openId',
                    'map.googleInitialized'
                ]
            }),
            withNgxsReduxDevtoolsPlugin({ disabled: false /*env.production*/ }),
            withNgxsLoggerPlugin({
                collapsed: false,
                disabled: env.production || true
            })
        ),
        provideAppRouting(),
        importProvidersFrom(
            PolygonSelectModule,
            ThemeSwitcherModule,
            PipeModule,
            LogoutModule,
            ResetPasswordModule,
            UserToolsModule,
            // custom elements
            FileDownloadModule,
            DigitizePointReportModule,
            DocumentLinkModule,
            QrCodeModule,
            ReportButtonModule,
            ReportEditButtonModule,
            ZoomButtonElementModule,
        ),
        { provide: ErrorHandler, useClass: DashGisErrorHandler },
        provideHttpClient(withInterceptorsFromDi()),
        provideOAuthClient(),
        // Native ng2 services
        BufferService,
        DashGisOverlay,
        DashGisOverlayContainer,
        DataFormService,
        DeviceService,
        DigitizePointConfigService,
        DigitizePointService,
        DigitizePointLauncherService,
        DownloadService,
        FeatureService,
        GoogleMapService,
        GsptService,
        HotKeyService,
        IconLoaderService,
        LineIsolationService,
        LiveSearchService,
        LocationService,
        LoggerService,
        MapCommandLocatorService,
        MapConfigService,
        MapInteractionsService,
        MapService,
        MeasureService,
        OrientationService,
        QrCodeService,
        QueryService,
        RedlineConfigService,
        RedlineService,
        ReportDisplayService,
        ReportService,
        ReportVideoLauncherService,
        ResizableListenerService,
        RestService,
        SearchService,
        WindowRefService,
        ZoomService,
        ZoomLatLonService,
        AuthService,
        { provide: OAuthStorage, useClass: OpenIdStorageShim, deps: [ Injector ] },
        { provide: OAuthLogger, useClass: ConsoleLoggerService },
        { provide: IsAuthenticatedTokens.get('password'), useClass: PasswordAuthenticatedService },
        { provide: IsAuthenticatedTokens.get('oidc'), useClass: OpenIdAuthenticatedService },
        { provide: APP_BASE_HREF, useValue: env.baseHref },
        { provide: LOGGER_PROVIDERS, useClass: ConsoleLoggerService, multi: true },
        { provide: LOGGER_PROVIDERS, useClass: BackendLoggerService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CheckTokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ForcePasswordChangeInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
        provideJwtConfig({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS, useFactory: csJwtOptionsProvider, deps: [ Store, OAuthService ]
            }
        }),
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'fill',
                floatLabel: 'auto',
                subscriptSizing: 'dynamic'
            }
        },
        {
            provide: MAT_SELECT_CONFIG,
            useValue: {
                panelWidth: null
            }
        },
        {
            provide: MAT_TABS_CONFIG,
            useValue: {
                stretchTabs: false
            }
        },
        {
            provide: LAYOUT_CONFIG,
            useValue: {
                addFlexToParent: false,
                addOrientationBps: true,
                disableDefaultBps: false,
                disableVendorPrefixes: false,
                serverLoaded: false,
                useColumnBasisZero: false
            }
        },
        { provide: STORAGE_ENGINE, useClass: LocalStorageProxy, deps: [ Injector ] },
        provideLuxonDateAdapter(),
        { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false, firstDayOfWeek: 7 } },
        { provide: ATTACH_COMMAND, useFactory: attachFactory },
        { provide: CLEAR_BUFFER_COMMAND, useFactory: clearBufferFactory },
        { provide: COPY_LAT_LON_COMMAND, useFactory: copyLatLonFactory },
        { provide: CREATE_BUFFER_COMMAND, useFactory: createBufferFactory },
        { provide: DATA_REPORT_COMMAND, useFactory: dataReportFactory },
        { provide: DESELECT_COMMAND, useFactory: deselectFactory },
        { provide: DIGITIZE_POINT_COMMAND, useFactory: digitizePointFactory },
        { provide: LAUNCH_GOOGLE_MAPS_COMMAND, useFactory: launchGoogleMapsFactory },
        { provide: LAUNCH_URL_COMMAND, useFactory: launchUrlFactory },
        { provide: LIVE_SEARCH_COMMAND, useFactory: liveSearchFactory },
        { provide: LOGOUT_COMMAND, useFactory: logoutFactory },
        { provide: MARKUP_COMMAND, useFactory: markupFactory },
        { provide: MEASURE_COMMAND, useFactory: measureFactory },
        { provide: PAN_COMMAND, useFactory: panFactory },
        { provide: POLYGON_SELECT_COMMAND, useFactory: polygonSelectFactory },
        { provide: PRINT_COMMAND, useFactory: printFactory },
        { provide: RELOAD_COMMAND, useFactory: reloadFactory },
        { provide: SEARCH_COMMAND, useFactory: searchFactory },
        { provide: SELECT_COMMAND, useFactory: selectFactory },
        { provide: SELECT_WITHIN_BUFFER_COMMAND, useFactory: selectWithinBufferFactory },
        { provide: ZOOM_EXTENTS_COMMAND, useFactory: zoomExtentsFactory },
        { provide: ZOOM_IN_COMMAND, useFactory: zoomInFactory },
        { provide: ZOOM_OUT_COMMAND, useFactory: zoomOutFactory },
        { provide: ZOOM_LAT_LON_COMMAND, useFactory: zoomLatLonFactory },
        provideAppInitializer(() => {
            inject(ReportVideoLauncherService).init();
        })
    ]
}).catch((err) => {
    console.error(err);
});
